import { NetworkStatus, useQuery } from '@apollo/client';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useErrorBoundary } from 'react-error-boundary';
import { format as formatDateTimeLocal, subWeeks } from 'date-fns';
import { useFragment as getFragmentData } from '@/graphql';
import type {
    SoundClipsQuery,
    SoundClipsQueryVariables,
    SoundClipTypeFieldsFragment,
} from '@/graphql/types';
import { SoundClipsDocument, SoundClipTypeFieldsFragmentDoc } from '@/graphql/types';
import { parseAndNormalizeDateRangeInTimezone } from '../../../utils/date-range';
import { createAfterCursor, extractNodes } from '../../../utils/relay';
import { NotFoundError } from '../NotFoundPage';
import { useUpdateSearchParams } from '../../../hooks/useUpdateSearchParams';
import { useDateRangeFilter } from '../../../hooks/useDateRangeFilter';
import {
    LOCAL_DATE_TIME_WITH_MILLISECONDS_FORMAT,
    presignedURLRefreshInterval,
} from '../../../constants';
import {
    useResetPageOnFilterChange,
    type UseOrderAndPaginationControlReturn,
} from '../../table/hooks';

export type SoundClipNode = SoundClipTypeFieldsFragment;
type SoundClipEdges = NonNullable<NonNullable<SoundClipsQuery['soundClips']>['edges']>;

interface SoundClipsFiltersState {
    mpPk: string;
    startDate: string;
    endDate: string;
}

interface SoundClipsFiltrationControl {
    filters: SoundClipsFiltersState;
    setMeasuringPointFilter: (value: string) => void;
    setDateRangeFilter: (startDate: string, endDate: string) => void;
    clearDateFilters: () => void;
}

const UNBOUNDED_START_TIME = '0';
const UNBOUNDED_END_TIME = String(Number.MAX_SAFE_INTEGER);

function formatEpochAsQueryDateTime(value: number): string {
    return formatDateTimeLocal(value, LOCAL_DATE_TIME_WITH_MILLISECONDS_FORMAT);
}

function getLastWeekDateTimeLocal(): string {
    return formatEpochAsQueryDateTime(subWeeks(new Date(), 1).getTime());
}

function createInitialFilters(searchParams: URLSearchParams): SoundClipsFiltersState {
    const startDateParam = searchParams.get('startDate');
    const endDateParam = searchParams.get('endDate');

    return {
        mpPk: searchParams.get('mpPk') ?? '',
        startDate: startDateParam ?? getLastWeekDateTimeLocal(),
        endDate: endDateParam ?? '',
    };
}

function serializeFiltersForComparison(filters: SoundClipsFiltersState): string {
    return JSON.stringify(filters);
}

export function useSoundClipsFiltrationControl(): SoundClipsFiltrationControl {
    const { searchParams, updateSearchParams } = useUpdateSearchParams();

    const [filters, setFilters] = useState<SoundClipsFiltersState>(() =>
        createInitialFilters(searchParams)
    );

    const handleFilterChange = useCallback(
        (key: keyof SoundClipsFiltersState, value: string) => {
            setFilters((prevState) => {
                if (prevState[key] === value) {
                    return prevState;
                }

                return {
                    ...prevState,
                    [key]: value,
                };
            });
            updateSearchParams(key, value);
        },
        [updateSearchParams]
    );

    const setDateRangeFilter = useDateRangeFilter(setFilters);

    return {
        filters,
        setMeasuringPointFilter(value: string) {
            return handleFilterChange('mpPk', value);
        },
        setDateRangeFilter,
        clearDateFilters() {
            setDateRangeFilter('', '');
        },
    };
}

function createSoundClipsQueryVariables({
    currentPage,
    pageSize,
    orderBy,
    mpPk,
    startDate,
    endDate,
    timezone,
}: {
    currentPage: number;
    pageSize: number;
    orderBy: string | null;
    mpPk: string;
    startDate: string;
    endDate: string;
    timezone: string;
}): SoundClipsQueryVariables {
    const dateRange = parseAndNormalizeDateRangeInTimezone({ startDate, endDate }, timezone);
    const resolvedStartTime = dateRange.startDate
        ? String(dateRange.startDate.getTime())
        : UNBOUNDED_START_TIME;
    const resolvedEndTime = dateRange.endDate
        ? String(dateRange.endDate.getTime())
        : UNBOUNDED_END_TIME;

    return {
        measuringPoint: mpPk,
        startTime: resolvedStartTime,
        endTime: resolvedEndTime,
        first: pageSize,
        after: createAfterCursor(currentPage, pageSize),
        orderBy,
    };
}

export function getSoundClipNodes(edges: SoundClipEdges | null | undefined): SoundClipNode[] {
    const maskedNodes = extractNodes(edges);
    return getFragmentData(SoundClipTypeFieldsFragmentDoc, maskedNodes);
}

export default function useSoundClipQueryGenerator(
    orderAndPaginationControl: UseOrderAndPaginationControlReturn,
    filtrationControl: SoundClipsFiltrationControl,
    timezone: string,
    skip = false
) {
    const [soundClips, setSoundClips] = useState<SoundClipNode[]>([]);
    const isPolling = useRef<boolean>(false);
    const { showBoundary } = useErrorBoundary();
    const { currentPage, pageSize, orderBy, setCurrentPage, setTotalCount } =
        orderAndPaginationControl;
    const {
        filters: { mpPk, startDate, endDate },
    } = filtrationControl;
    const committedFilterSignature = useMemo(
        () => serializeFiltersForComparison(filtrationControl.filters),
        [filtrationControl.filters]
    );

    const effectiveCurrentPage = useResetPageOnFilterChange(
        committedFilterSignature,
        currentPage,
        setCurrentPage
    );

    const queryVariables = useMemo(
        () =>
            createSoundClipsQueryVariables({
                currentPage: effectiveCurrentPage,
                pageSize,
                orderBy,
                mpPk,
                startDate,
                endDate,
                timezone,
            }),
        [effectiveCurrentPage, pageSize, orderBy, mpPk, startDate, endDate, timezone]
    );

    const { data, loading, startPolling, stopPolling, networkStatus, error } = useQuery<
        SoundClipsQuery,
        SoundClipsQueryVariables
    >(SoundClipsDocument, {
        skip: skip || !mpPk,
        variables: queryVariables,
        fetchPolicy: 'network-only',
    });

    useEffect(() => {
        if (!error) {
            return;
        }

        if (
            error.graphQLErrors.some(
                (gqlError) => gqlError.extensions?.code === 'NO_MEASURING_POINT_FOUND'
            )
        ) {
            showBoundary(new NotFoundError());
            return;
        }

        showBoundary(error);
    }, [error, showBoundary]);

    useEffect(() => {
        if (data?.soundClips) {
            setSoundClips(getSoundClipNodes(data.soundClips.edges));
            setTotalCount(data.soundClips.totalCount || 0);
            startPolling(presignedURLRefreshInterval);
            isPolling.current = true;
        }

        return () => {
            if (isPolling.current) {
                stopPolling();
            }
        };
    }, [data, setTotalCount, startPolling, stopPolling]);

    useEffect(() => {
        if (!mpPk) {
            setSoundClips([]);
            setTotalCount(0);
        }
    }, [mpPk, setTotalCount]);

    return {
        soundClips,
        loading: loading && networkStatus === NetworkStatus.loading,
        updating: loading && networkStatus === NetworkStatus.setVariables,
    };
}
